import React, { FC, useRef } from 'react';
import {
    Radio,
    MenuItem,
    Checkbox,
    CircularProgress
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import CloseIcon from '@material-ui/icons/Close';
import styles from "../../../hygienist/ProfilePage/components/components.module.scss";
import { HygienistSkill, roles } from "utils/constants";
import MaterialInput from "../../../../components/Inputs/MaterialInput";
import { MaterialSelector } from "../../../../components/Selector";
import AdditionalSkills from "../../../../components/AdditionalSkills";
import Languages from "components/Languages";
import { Language } from "api/Api";
import { SimpleModal } from "components/Modal";
import LeftHandedModal from "./LeftHandedModal";
import AssistantSkillsCheckModal from "./AssistantSkillsCheckModal";

interface QualificationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (event: React.FormEvent) => void;
    roleNumber: number;
    province?: string;
    skills: HygienistSkill[];
    licensed: boolean | undefined;
    setLicensed: (value: boolean | undefined) => void;
    disabledSkills: boolean;
    setDisabledSkills: (value: boolean) => void;
    checkedSkills: HygienistSkill[];
    setCheckedSkills: (skills: HygienistSkill[]) => void;
    isLeftHanded: boolean;
    setIsLeftHanded: (value: boolean) => void;
    userQualification?: string;
    setUserQualification: (value: string) => void;
    errorMessage: any;
    setErrorMessage: (errors: any) => void;
    // Languages props
    commonLanguages: Language[];
    otherLanguages: Language[];
    initialSelectedLanguages: Language[];
    selectedLanguagesRef: React.MutableRefObject<number[]>;
    // Left-handed modal
    isLeftHandedModal: boolean;
    setIsLeftHandedModal: (value: boolean) => void;
    // Assistant skills check modal
    isAssistantSkillsCheckModal: boolean;
    setAssistantSkillsCheckModal: (value: boolean) => void;
    handleAssistantSkillsCheckModalClose: () => void;
    school: string;
    setSchool: (value: string) => void;
    licenseNumber: string;
    setLicenseNumber: (value: string) => void;
    loading?: boolean;
}

export const QualificationModal: FC<QualificationModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    roleNumber,
    province,
    skills,
    licensed,
    setLicensed,
    disabledSkills,
    setDisabledSkills,
    checkedSkills,
    setCheckedSkills,
    isLeftHanded,
    setIsLeftHanded,
    userQualification,
    setUserQualification,
    errorMessage,
    setErrorMessage,
    commonLanguages,
    otherLanguages,
    initialSelectedLanguages,
    selectedLanguagesRef,
    isLeftHandedModal,
    setIsLeftHandedModal,
    isAssistantSkillsCheckModal,
    setAssistantSkillsCheckModal,
    handleAssistantSkillsCheckModalClose,
    school,
    setSchool,
    licenseNumber,
    setLicenseNumber,
    loading = false
}) => {
    const htmlElRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const licenseSkill = skills.find(skill => skill.license && !skill.qualification);
    const qualificationSkills = skills.filter((skill: HygienistSkill) => skill.qualification && !skill.license);
    const licenseQualificationSkill = skills.find(skill => skill.license && skill.qualification);

    let license: string | undefined = undefined;
    let label = '';
    let labelDescription = '';
    let noLicenseLabel = '';
    let noLicenselabelDescription = '';

    if (roleNumber === roles.HYGIENIST) {
        license = 'Hygiene License #';
    } else if (roleNumber === roles.ASSISTANT) {
        license = 'Assistant Certificate #';
    }

    if (licenseSkill) {
        label = licenseSkill.name === 'RDH'
            ? 'I am an Actively Licensed Registered Dental Hygienist (RDH)'
            : 'I am a Certified or Registered Dental Assistant';
        labelDescription = licenseSkill.name === 'RDH'
            ? `This means you are formally trained, qualified and have an active RDH license in good standing with your regulatory body and can legally work as a Dental Hygienist in ${province}.`
            : `This means you have received formal training as a Dental Assistant and have an active Certification or Registration in good standing in ${province}.`;
    }

    if (licenseQualificationSkill) {
        noLicenseLabel = `I am not a Certified or Registered Dental Assistant`;
        noLicenselabelDescription = `The means that you have been trained on-the-job to a high level of competency and professionalism, and are skilled and qualified to work as a dental assistant in ${province}`;
    }

    const schoolHandler = (e: any) => {
        setSchool(e.target.value);
        if (e.target.value) {
            setErrorMessage({ ...errorMessage, education: '' });
        }
    };

    const licenseHandler = (e: any) => {
        setLicenseNumber(e.target.value);
        if (e.target.value) {
            setErrorMessage({ ...errorMessage, license: '' });
        }
    };

    const qualificationHandler = (e: any) => {
        setUserQualification(JSON.parse(e.target.value).name);
        if (e.target.value) {
            setErrorMessage({ ...errorMessage, qualification: '' });
        }
    };

    return (
        <>
            <div className={styles.wizardModalOverlay} onClick={onClose}>
                <div 
                    className={styles.wizardModalContainer} 
                    onClick={(e) => e.stopPropagation()}
                    style={{ maxHeight: '90vh' }}
                >
                    {/* Header */}
                    <div className={styles.wizardModalHeader}>
                        <div className={styles.wizardHeaderContent}>
                            <h1 className={styles.wizardModalTitle}>Qualification & Skills</h1>
                            <p className={styles.wizardModalSubtitle}>
                                Let Dental Offices know about your specific training and skills
                            </p>
                        </div>
                        <button className={styles.wizardCloseButton} onClick={onClose} aria-label="Close modal">
                            <CloseIcon />
                        </button>
                    </div>

                    {/* Body */}
                    <form 
                        id="qualification-form"
                        className={styles.wizardModalBody} 
                        onSubmit={onSubmit}
                        style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}
                    >
                        {/* License/Qualification Radio */}
                        {licenseSkill && (
                            <div 
                                className={`${styles.wizardRadioOption} ${licensed === true ? styles.selected : ''}`}
                                onClick={() => {
                                    if (!licenseQualificationSkill && licensed === true) {
                                        setDisabledSkills(true);
                                        setLicensed(undefined);
                                    } else {
                                        setDisabledSkills(false);
                                        setLicensed(true);
                                    }
                                    setCheckedSkills([]);
                                    setErrorMessage({ ...errorMessage, licensed: false });
                                }}
                            >
                                {licenseQualificationSkill ? (
                                    <Radio
                                        checked={licensed === true}
                                        value={licensed}
                                        icon={<img id="circleUnchecked" className="hidden-icon" alt="" src={"img/circleUnchecked.svg"} />}
                                        checkedIcon={<img id="circleChecked" className="hidden-icon" alt="" src={"img/circleChecked.svg"} />}
                                        style={{ padding: 0 }}
                                    />
                                ) : (
                                    <Checkbox
                                        checked={licensed === true}
                                        icon={<img id="checkboxUnchecked" className="hidden-icon" alt="" src={"img/checkboxUnchecked.svg"} />}
                                        checkedIcon={<img id="checkboxChecked" className="hidden-icon" alt="" src={"img/checkboxChecked.svg"} />}
                                        style={{ padding: 0 }}
                                    />
                                )}
                                <div className={styles.radioContent}>
                                    <div className={styles.radioLabel} style={{ color: errorMessage?.licensed ? '#f44336' : '' }}>
                                        {label}
                                    </div>
                                    <div className={styles.radioDescription}>{labelDescription}</div>
                                </div>
                            </div>
                        )}

                        {/* Qualification Dropdown.
                            T2-5789: stay enabled once the user has picked either
                            "Licensed" (true) or "Not certified" (false). Previously
                            `!licensed` also disabled the false branch, leaving
                            unlicensed assistants with required validation they
                            could not satisfy. Initial state (undefined) still
                            keeps it disabled so the radio is the entry point. */}
                        {!!qualificationSkills.length && (
                            <div className={styles.wizardInputContainer}>
                                <MaterialSelector
                                    disabled={disabledSkills || licensed === undefined}
                                    id={'qualification'}
                                    name={'qualification'}
                                    label={'Qualification'}
                                    errorMessage={errorMessage?.qualification}
                                    onChange={qualificationHandler}
                                >
                                    {qualificationSkills.map((skill: HygienistSkill) => (
                                        <MenuItem key={skill.id} value={JSON.stringify(skill)} id={skill.name}>
                                            {skill.label}
                                        </MenuItem>
                                    ))}
                                </MaterialSelector>
                            </div>
                        )}

                        {/* School Input — same T2-5789 unlock as the qualification dropdown. */}
                        <div className={styles.wizardInputContainer}>
                            <MaterialInput
                                disabled={disabledSkills || licensed === undefined}
                                label="School / Institute"
                                id={'education'}
                                name={"education"}
                                type={"text"}
                                value={school}
                                onChange={schoolHandler}
                                error={!!errorMessage?.education}
                                helperText={!!errorMessage?.education && errorMessage.education}
                            />
                        </div>

                        {/* License Number Input */}
                        {license && (
                            <div className={styles.wizardInputContainer}>
                                <MaterialInput
                                    disabled={disabledSkills || !licensed}
                                    inputRef={htmlElRef}
                                    label={license}
                                    value={licenseNumber}
                                    onChange={licenseHandler}
                                    type={"text"}
                                    id={'license'}
                                    name={"license"}
                                    error={!!errorMessage?.license}
                                    helperText={
                                        roleNumber === roles.ASSISTANT
                                            ? userQualification === 'CDA2'
                                                ? (!!errorMessage?.license && errorMessage.license) || '(Required)'
                                                : '(Optional)'
                                            : !!errorMessage?.license && errorMessage.license
                                    }
                                />
                            </div>
                        )}

                        {/* Left-handed checkbox for hygienists */}
                        {roleNumber === roles.HYGIENIST && (
                            <div 
                                className={`${styles.wizardRadioOption} ${isLeftHanded ? styles.selected : ''}`}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}
                                    onClick={() => {
                                        if (!disabledSkills && licensed) {
                                            setIsLeftHanded(!isLeftHanded);
                                        }
                                    }}
                                >
                                    <Checkbox
                                        checked={isLeftHanded}
                                        disabled={disabledSkills || !licensed}
                                        icon={<img id="checkboxUnchecked" className="hidden-icon" alt="" src={"img/checkboxUnchecked.svg"} />}
                                        checkedIcon={<img id="checkboxChecked" className="hidden-icon" alt="" src={"img/checkboxChecked.svg"} />}
                                        style={{ padding: 0 }}
                                    />
                                    <div className={styles.radioContent}>
                                        <div className={styles.radioLabel}>I'm left-handed</div>
                                    </div>
                                </div>
                                <InfoOutlinedIcon 
                                    style={{ color: '#6B7280', cursor: 'pointer' }} 
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsLeftHandedModal(true);
                                    }} 
                                />
                            </div>
                        )}

                        {/* Non-licensed option for assistants */}
                        {licenseQualificationSkill && (
                            <div 
                                className={`${styles.wizardRadioOption} ${licensed === false ? styles.selected : ''}`}
                                onClick={() => {
                                    setDisabledSkills(false);
                                    setLicensed(false);
                                    setCheckedSkills([]);
                                }}
                            >
                                <Radio
                                    checked={licensed === false}
                                    icon={<img id="circleUnchecked" className="hidden-icon" alt="" src={"img/circleUnchecked.svg"} />}
                                    checkedIcon={<img id="circleChecked" className="hidden-icon" alt="" src={"img/circleChecked.svg"} />}
                                    style={{ padding: 0 }}
                                />
                                <div className={styles.radioContent}>
                                    <div className={styles.radioLabel}>{noLicenseLabel}</div>
                                    <div className={styles.radioDescription}>{noLicenselabelDescription}</div>
                                </div>
                            </div>
                        )}

                        {/* Languages Section */}
                        <div className={styles.wizardFormSection} style={{ marginTop: '20px' }}>
                            <Languages
                                commonLanguages={commonLanguages}
                                otherLanguages={otherLanguages}
                                initialSelected={initialSelectedLanguages}
                                error={errorMessage?.languages}
                                onClearError={() => setErrorMessage({ ...errorMessage, languages: '' })}
                                onChange={(langIds) => { selectedLanguagesRef.current = langIds; }}
                            />
                        </div>

                        {/* Additional Skills */}
                        <AdditionalSkills
                            key={`${licensed}-${disabledSkills}`}
                            disabled={disabledSkills}
                            skills={skills}
                            checkedSkills={checkedSkills}
                            state={province || ''}
                            qualificationSkill={qualificationSkills.find(skill => skill.name === userQualification)}
                        />

                        {/* Hidden submit button for form */}
                        <button type="submit" style={{ display: 'none' }} />
                    </form>

                    {/* Footer */}
                    <div className={styles.wizardModalFooter}>
                        <button className={styles.wizardCancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            className={styles.wizardSaveButton}
                            type="submit"
                            form="qualification-form"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Left-handed Modal */}
            <SimpleModal isOpen={isLeftHandedModal}>
                <LeftHandedModal onCloseButtonClick={() => setIsLeftHandedModal(false)} />
            </SimpleModal>

            {/* Assistant Skills Check Modal */}
            <SimpleModal isOpen={isAssistantSkillsCheckModal}>
                <AssistantSkillsCheckModal
                    onClose={() => setAssistantSkillsCheckModal(false)}
                    onSaveAndContinue={handleAssistantSkillsCheckModalClose}
                />
            </SimpleModal>
        </>
    );
};

export default QualificationModal;
